import React, { useEffect, useState, lazy, Suspense } from "react";
import AppLayout from "./AppLayout/AppLayout";
import "./App.css";
import "../Content/css/style.css";
import "../Content/css/colors/main.css";
import "react-toastify/dist/ReactToastify.css";
import { ToastContainer } from "react-toastify";
import { BrowserRouter, Redirect, Route, Switch } from "react-router-dom";
import { AuthProvider } from "./AuthContext";
import { onAuthStateChanged, signOut, multiFactor } from "firebase/auth";
import Signup from "./Signup/Signup";
import { AppContextProvider } from "./UserContext/TransporterCompareContext";
import ReactGA from "react-ga";
import mixpanel from "mixpanel-browser";
import { ENV, GOOGLE_ID, MIXPANEL_TOKEN } from "../Constants";
import { UserDetailsContext } from "./UserDetailsContext/UserDetailsContext";
import { auth } from "./firebase/firebase";
import Login from "./Login/Login";
import PrivateRoute from "../PrivateRoute";
import Loader from "./Loader/Loader";
import { EditableEmailsProvider } from "../Context/EditableEmailsContext";
import { MyCompanyProfileDetailsProvider } from "../MyCompanyProfileContext";
import { SidebarProvider } from "../Context/SidebarContext";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import PhoneRecovery from "./Login/PhoneRecovery";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";

const queryClient = new QueryClient();
const ForgotPassword = lazy(() => import("./ForgotPassword/ForgotPassword"));
const ChangePassword = lazy(() => import("./ForgotPassword/ChangePassword"));
const ResetPassword = lazy(() => import("./ResetPassword/ResetPassword"));

import "./../Content/css/bootstrap.css";
import { setupAxiosInterceptors } from "../Axios/setupAxiosInterceptors";
import GlobalErrorBoundary from "./Shared/GlobalErrorBoundary/GlobalErrorBoundary";

const gaId = GOOGLE_ID;
ReactGA.initialize(gaId);

const mixpanelId = MIXPANEL_TOKEN;
mixpanel.init(mixpanelId);

setupAxiosInterceptors();

export const MFA_VERIFIED_KEY = "mfa_login_verified";

const checkAuth = async (user) => {
  if (!user) return false;

  // Existing sessions: if they were already verified/logged in before
  // this feature shipped, respect that and let them through
  const mfaVerified = localStorage.getItem(MFA_VERIFIED_KEY) === "true";
  if (mfaVerified) return true;

  // New sessions: require email to be verified
  if (!user.emailVerified) return false;

  try {
    const tokenResult = await user.getIdTokenResult(true);
    if (tokenResult.signInProvider === "custom") return true;

    const firebaseClaim = tokenResult.claims?.firebase;
    const mfaInToken = !!(
      tokenResult.signInSecondFactor ||
      firebaseClaim?.sign_in_second_factor
    );

    if (mfaInToken) {
      localStorage.setItem(MFA_VERIFIED_KEY, "true");
      return true;
    }

    const enrolled = multiFactor(user).enrolledFactors ?? [];
    if (enrolled.length > 0) {
      localStorage.removeItem(MFA_VERIFIED_KEY);
      await signOut(auth);
      return false;
    }

    return true;
  } catch (err) {
    console.error("[App] checkAuth error:", err);
    return false;
  }
};
const NoNetwork = () => {
  const [currentUser, setCurrentUser] = useState(null);
  const [show, setShow] = useState(true);
  const [loginPage, setLoginPage] = useState(false);
  const [isAuth, setIsAuth] = useState(false);

  // useEffect(() => {
  //   const unsubscribe = onAuthStateChanged(auth, async (user) => {
  //     setShow(true);
  //     if (user) {
  //       const authOk = await checkAuth(user);
  //       if (authOk) {
  //         setCurrentUser(user);
  //         setIsAuth(true);
  //         sessionStorage.setItem("uid", user.uid);
  //       } else {
  //         setCurrentUser(null);
  //         setIsAuth(false);
  //       }
  //     } else {
  //       setCurrentUser(null);
  //       setIsAuth(false);
  //       localStorage.removeItem(MFA_VERIFIED_KEY);
  //       setLoginPage(true);
  //     }
  //     setShow(false);
  //   });
  //   return () => unsubscribe();
  // }, []);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setShow(true);
      if (user) {
        const authOk = await checkAuth(user);
        if (authOk) {
          setCurrentUser(user);
          setIsAuth(true);
          sessionStorage.setItem("uid", user.uid);
        } else {
          setCurrentUser(null);
          setIsAuth(false);
        }
      } else {
        setCurrentUser(null);
        setIsAuth(false);
        localStorage.removeItem(MFA_VERIFIED_KEY);
        setLoginPage(true);
      }
      setShow(false);
    });

    const forceAuthIn = () => {
      setCurrentUser(auth.currentUser);
      setIsAuth(true);
      setShow(false);
    };
    window.addEventListener("lorri:force-auth-in", forceAuthIn);

    return () => {
      unsubscribe();
      window.removeEventListener("lorri:force-auth-in", forceAuthIn);
    };
  }, []);
  useEffect(() => {
    localStorage.removeItem("StatusBucket");
    localStorage.removeItem("activeTab");
    localStorage.removeItem("currentPage");
    localStorage.removeItem("rowsPerPage");
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      {ENV !== "prod" && ENV !== "production" && (
        <ReactQueryDevtools initialIsOpen={false} />
      )}
      <ToastContainer style={{ zIndex: 999999 }} />
      {show ? (
        <Loader show={show} />
      ) : (
        <AppContextProvider>
          <BrowserRouter>
            <GlobalErrorBoundary>
              <AuthProvider value={{ currentUser }}>
                {window.location.pathname === "/" ? (
                  <Redirect from="/" to={`/login${window.location.search}`} />
                ) : null}
                {!isAuth ? (
                  <Suspense fallback={<div />}>
                    <Switch>
                      <Route path="/signup" component={Signup} exact />
                      <Route path="/login" component={Login} exact />
                      <Route path="/forgot-password" component={ForgotPassword} exact />
                      <Route path="/reset-password" component={ResetPassword} exact />
                      <Route path="/phone-recovery" component={PhoneRecovery} exact />
                      <Route path="/change-password" component={ChangePassword} exact />
                      {loginPage && <Route path="/" component={Login} />}
                    </Switch>
                  </Suspense>
                ) : (
                  <PrivateRoute>
                    <UserDetailsContext>
                      <MyCompanyProfileDetailsProvider>
                        <EditableEmailsProvider>
                          <SidebarProvider>
                            <AppLayout />
                          </SidebarProvider>
                        </EditableEmailsProvider>
                      </MyCompanyProfileDetailsProvider>
                    </UserDetailsContext>
                  </PrivateRoute>
                )}
              </AuthProvider>
            </GlobalErrorBoundary>
          </BrowserRouter>
        </AppContextProvider>
      )}
    </QueryClientProvider>
  );
};

const App = () => <NoNetwork />;
export default App;